import { TNotice } from "@sundae/ui-toolkit/dist/types/components/molecules/Notice/Notice";
import { AssetAmount } from "@sundaeswap/asset";
import { Fraction, TFractionLike } from "@sundaeswap/fraction";
import { normalizeInputValue } from "../components/OrderComponents/AssetInputs/utils";
import {
  MAX_PRICE_IMPACT_CHECKBOX_CONFIRMATION,
  MAX_PRICE_IMPACT_SUCCESS,
  MAX_PRICE_IMPACT_WARNING,
  MAX_SLIPPAGE_TOLERANCE,
  MIN_SLIPPAGE_TOLERANCE,
} from "../constants/exchange.constants";
import { PoolFragmentFragment } from "../gql/generated/stats2.sdk";
import { ExchangeMode } from "../stores";
import { IAssetMetaData } from "../types/Asset.types";
import { getAssetsRatio } from "./assets.utils";
import { divideBy, multiplyBy, numberI18n } from "./number-format";

export enum ELimitValueRelativeToParity {
  equalToParity = "equalToParity",
  greaterThanParity = "greaterThanParity",
  lessThanParity = "lessThanParity",
}

export type TGetExchangeValue = {
  inputReserve: bigint;
  lpFee: TFractionLike;
  outputReserve: bigint;
  value: bigint;
};

/**
 * Returns boolean whether there's insufficient quantity of an asset.
 */
export const isSufficientQuantity = ({
  quantity,
  value,
}: {
  quantity?: number;
  value: string | number;
}) => {
  if (!quantity) return false;

  const parsedValue = typeof value === "string" ? Number(value) : value;

  return quantity >= parsedValue;
};

/**
 * Calculates the transaction liquidity pool (LP) fee based on the values of two assets and a fee percentage.
 *
 * The function expects three parameters: `valueA`, `valueB`, and `fee`.
 * - `valueA` and `valueB` are `TFractionLike` values representing the values of two assets.
 * - `fee` is a string representing the fee percentage (e.g., `"0.3"` for 0.3%).
 *
 * The function calculates the LP fee as the value of asset B multiplied by the fee percentage, divided by the ratio of asset B to asset A.
 *
 * @param {TFractionLike} valueA - The value of asset A.
 * @param {TFractionLike} valueB - The value of asset B.
 * @param {string} fee - The fee percentage.
 *
 * @returns {Fraction | null} The calculated transaction LP fee, or null if any of the parameters is not provided.
 */
export const calculateTransactionLPFee = (
  valueA?: TFractionLike | null,
  valueB?: TFractionLike | null,
  fee?: string | null
) => {
  if (!valueA || !valueB || !fee) return null;

  const assetsRatio = getAssetsRatio(valueB, valueA); // aPerB

  if (!assetsRatio) return null;

  const valueBFraction = Fraction.asFraction(valueB);
  const formattedFee = divideBy(fee, 100); // GQL fee is returned as `"0.3"` (0.3% === 0.03)

  if (!formattedFee) return null;

  return valueBFraction.multiply(formattedFee).divide(assetsRatio);
};

/**
 * Based on the actual price impact, we wil return states in our UI that can be passed to any `<Text />` based component.
 */
export const getPriceImpactStatus = (priceImpact?: number) => {
  if (!priceImpact) return;

  if (multiplyBy(priceImpact, 100) <= MAX_PRICE_IMPACT_SUCCESS) {
    return "success";
  }

  if (multiplyBy(priceImpact, 100) <= MAX_PRICE_IMPACT_WARNING) {
    return "warning";
  }

  return "error";
};

/**
 * Determines whether to display a checkbox confirmation based on the price impact.
 * Returns true if the price impact exceeds the predefined maximum threshold.
 *
 * @param {Fraction} [priceImpact] - The price impact to evaluate.
 * @return {boolean} - True if checkbox confirmation should be displayed, false otherwise.
 */
export const getShowPriceImpactWarning = (priceImpact?: Fraction) => {
  return Boolean(priceImpact?.greaterThan(MAX_PRICE_IMPACT_CHECKBOX_CONFIRMATION));
};

/**
 * Transforms the `slippageTolerance` to a proper i18n string.
 */
export const handleSlippageToleranceI18N = (slippageTolerance: number | string) =>
  Number(
    numberI18n(Number(slippageTolerance), {
      maximumFractionDigits: 2,
    })
  );

/**
 * This function provides the proper `slippageTolerance` value for the Input inside the `ExchangeSettingsDialog` component.
 */
export const handleSlippageValueOnBlur = (slippageTolerance: number) => {
  if (slippageTolerance > MAX_SLIPPAGE_TOLERANCE || slippageTolerance < MIN_SLIPPAGE_TOLERANCE) {
    return handleSlippageToleranceI18N(
      Math.max(
        MIN_SLIPPAGE_TOLERANCE,
        Math.min(MAX_SLIPPAGE_TOLERANCE, slippageTolerance || MIN_SLIPPAGE_TOLERANCE)
      )
    );
  }

  return slippageTolerance;
};

/**
 * This function takes in a new value and compares it to the old and returns the relative delta as absolute number.
 */
export const calculatePercentageDelta = (newValue: Fraction, oldValue: Fraction) => {
  const decreasedValue = newValue.subtract(oldValue);
  return decreasedValue.equals(Fraction.ZERO)
    ? 0
    : Math.abs(decreasedValue.divide(oldValue).toNumber());
};

/**
 * Takes in the `limitValue` (input for a limit price inside a buy/sell order) and compares the delta to the current market price (parity).
 * Returns delta as absolute number as well as the indication whether the delta is positive, negative or equal compared to the parity.
 */
export const getLimitValueDelta = (limitValue: TFractionLike, parity: AssetAmount) => {
  const isLimitValueLessThanParity = parity.value.greaterThan(limitValue);
  const isLimitValueGreaterThanParity = parity.value.lessThan(limitValue);
  const limitValueEqualsParity = parity.value.equals(limitValue);
  return {
    absoluteLimitValueDelta: calculatePercentageDelta(
      Fraction.asFraction(limitValue),
      parity.value
    ),
    isLimitValueLessThanParity,
    isLimitValueGreaterThanParity,
    limitValueEqualsParity,
  };
};

/**
 * Takes in the `limitValue` (input for a limit price inside a buy/sell order) and compares the delta to the current market price (parity).
 * Returns necessary information around the limitValue and the tx.
 *
 * absoluteLimitValueDelta: delta as absolute number;
 * context: i18n context;
 * deltaRelativeToParity: delta relative to the parity (greaterThan || lessThan || equal);
 * type === variant;
 * warning: i18n string;
 */
export const getLimitOrderMetaData = ({
  exchangeMode,
  limitValue,
  parity,
}: {
  exchangeMode: ExchangeMode;
  limitValue?: string;
  parity?: AssetAmount;
}):
  | (Pick<TNotice, "type"> & {
      absoluteLimitValueDelta: number;
      deltaRelativeToParity: ELimitValueRelativeToParity;
      warning: string;
      context?: string;
    })
  | undefined => {
  if (!limitValue || !parity) return undefined;

  const {
    absoluteLimitValueDelta,
    isLimitValueLessThanParity,
    isLimitValueGreaterThanParity,
    limitValueEqualsParity,
  } = getLimitValueDelta(limitValue, parity);

  if (isLimitValueLessThanParity) {
    return {
      absoluteLimitValueDelta,
      deltaRelativeToParity: ELimitValueRelativeToParity.lessThanParity,
      warning: "limitValueLessThanParity",
      type: exchangeMode === ExchangeMode.buy ? "success" : "error",
      context: undefined,
    };
  }

  if (isLimitValueGreaterThanParity) {
    return {
      absoluteLimitValueDelta,
      deltaRelativeToParity: ELimitValueRelativeToParity.greaterThanParity,
      warning: "limitValueGreaterThanParity",
      type: exchangeMode === ExchangeMode.buy ? "error" : "success",
      context: undefined,
    };
  }

  if (limitValueEqualsParity) {
    return {
      absoluteLimitValueDelta,
      deltaRelativeToParity: ELimitValueRelativeToParity.equalToParity,
      type: "info",
      warning: "limitValueEqualsParity",
      context: undefined,
    };
  }
};

export const getPoolReserves = (
  pool?: Pick<PoolFragmentFragment, "fee" | "quantityA" | "quantityB">
) => {
  if (!pool) {
    return {
      lpFee: null,
      inputReserve: null,
      outputReserve: null,
    };
  }

  return {
    lpFee: divideBy(pool.fee),
    inputReserve: pool.quantityA ? BigInt(pool.quantityA) : 0n,
    outputReserve: pool.quantityB ? BigInt(pool.quantityB) : 0n,
  };
};

/**
 * Converts a given AssetAmount to a formatted string based on a given locale.
 *
 * @param {AssetAmount<IAssetMetaData>} given - The asset amount to format.
 * @param {string} currentLocale - The locale based on which the number should be formatted.
 * @returns {string} The formatted string representation of the asset amount.
 */
export const givenToFormattedValue = (
  given: AssetAmount<IAssetMetaData> | undefined,
  currentLocale: string
) => {
  if (!given) return "";
  return numberI18n(
    Number(normalizeInputValue(given)),
    {
      maximumFractionDigits: given.decimals,
    },
    currentLocale
  );
};
